/*
 * Shared modal controller
 * - Consolidates open/close state handling
 * - Prevents scroll-lock conflicts across stacked modals
 * - Supports delegated close triggers, Escape close, and optional focus trap
 */
(function () {
  if (window.SAModalController) return;

  var defaults = {
    openClass: 'is-open',
    visibleClass: '',
    ariaAttribute: 'aria-hidden',
    hiddenAttribute: false,
    openWithRaf: false,
    lockScroll: true,
    closeOnEscape: true,
    trapFocus: false,
    closeSelector: '',
    closeDelay: 0,
    openDisplay: '',
    closeDisplay: '',
    focusSelector: '',
    restoreFocus: true,
    appendToBody: true,
    inertAttribute: true,
    isolateBackground: true
  };

  var configMap = new WeakMap();
  var stateMap = new WeakMap();
  var modalStack = [];
  var bodyLockCount = 0;
  var isolatedElements = [];
  var isolationObserver = null;

  function getElement(target) {
    if (!target) return null;
    if (typeof target === 'string') return document.querySelector(target);
    return target;
  }

  function buildConfig(modal, options) {
    var existing = configMap.get(modal) || {};
    var merged = Object.assign({}, defaults, existing, options || {});
    configMap.set(modal, merged);
    return merged;
  }

  function addModalToStack(modal) {
    removeModalFromStack(modal);
    modalStack.push(modal);
  }

  function removeModalFromStack(modal) {
    modalStack = modalStack.filter(function (item) {
      return item !== modal;
    });
  }

  function lockBodyScroll() {
    bodyLockCount += 1;
    if (bodyLockCount > 1) return;

    if (document.body.dataset.saModalOverflow === undefined) {
      document.body.dataset.saModalOverflow = document.body.style.overflow || '';
    }
    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    if (bodyLockCount === 0) return;

    bodyLockCount -= 1;
    if (bodyLockCount > 0) return;

    document.body.style.overflow = document.body.dataset.saModalOverflow || '';
    delete document.body.dataset.saModalOverflow;
  }

  function restoreBackgroundIsolation() {
    if (isolationObserver) isolationObserver.disconnect();
    isolatedElements.forEach(function (record) {
      if (!record.element || !record.element.isConnected) return;
      if (record.hadInert) record.element.setAttribute('inert', '');
      else record.element.removeAttribute('inert');
      if (record.ariaHidden === null) record.element.removeAttribute('aria-hidden');
      else record.element.setAttribute('aria-hidden', record.ariaHidden);
    });
    isolatedElements = [];
  }

  function isolateElement(element, topModal) {
    if (!(element instanceof HTMLElement) || element === topModal) return;
    if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(element.tagName)) return;
    if (isolatedElements.some(function (record) { return record.element === element; })) return;
    isolatedElements.push({
      element: element,
      hadInert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden')
    });
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');
  }

  function refreshBackgroundIsolation() {
    restoreBackgroundIsolation();
    if (!modalStack.length) return;

    var topModal = modalStack[modalStack.length - 1];
    var config = configMap.get(topModal) || defaults;
    if (!config.isolateBackground) return;

    Array.prototype.forEach.call(document.body.children, function (element) {
      isolateElement(element, topModal);
    });

    if (!isolationObserver) {
      isolationObserver = new MutationObserver(function (records) {
        var currentTop = modalStack[modalStack.length - 1];
        if (!currentTop) return;
        records.forEach(function (record) {
          Array.prototype.forEach.call(record.addedNodes, function (node) {
            isolateElement(node, currentTop);
          });
        });
      });
    }
    isolationObserver.observe(document.body, { childList: true });
  }

  function setOpenState(modal, config, isOpen) {
    if (config.openClass) modal.classList.toggle(config.openClass, isOpen);
    if (config.visibleClass) modal.classList.toggle(config.visibleClass, isOpen);

    if (config.ariaAttribute) {
      modal.setAttribute(config.ariaAttribute, isOpen ? 'false' : 'true');
    }

    if (config.inertAttribute) {
      if (isOpen) modal.removeAttribute('inert');
      else modal.setAttribute('inert', '');
    }

    if (config.hiddenAttribute) {
      modal.hidden = !isOpen;
    }

    if (isOpen && config.openDisplay) {
      modal.style.display = config.openDisplay;
    }
    if (!isOpen && config.closeDisplay) {
      modal.style.display = config.closeDisplay;
    }
  }

  function getFocusableElements(modal) {
    var selectors = [
      'button:not([disabled])',
      '[href]:not([tabindex="-1"])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    var nodes = modal.querySelectorAll(selectors.join(','));
    return Array.prototype.slice.call(nodes).filter(function (node) {
      return node.offsetParent !== null && !node.hasAttribute('aria-hidden');
    });
  }

  function focusInitial(modal, config) {
    var target = null;
    if (config.focusSelector) {
      target = modal.querySelector(config.focusSelector);
    }
    if (!target && config.closeSelector) {
      target = modal.querySelector(config.closeSelector);
    }
    if (!target) {
      var focusables = getFocusableElements(modal);
      target = focusables[0];
    }
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  }

  function open(target, options) {
    var modal = getElement(target);
    if (!modal) return false;

    var config = buildConfig(modal, options);
    var alreadyOpen = modalStack.indexOf(modal) !== -1;
    var state = stateMap.get(modal) || {};
    if (!alreadyOpen) state.lastFocusedElement = document.activeElement;
    stateMap.set(modal, state);

    if (config.appendToBody && modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    addModalToStack(modal);

    if (config.lockScroll && !alreadyOpen) {
      lockBodyScroll();
    }

    var applyOpenState = function () {
      refreshBackgroundIsolation();
      setOpenState(modal, config, true);
      if (config.trapFocus && !alreadyOpen) {
        requestAnimationFrame(function () {
          if (isOpen(modal, config)) focusInitial(modal, config);
        });
      }
    };

    if (config.openWithRaf) {
      if (config.hiddenAttribute) modal.hidden = false;
      if (config.openDisplay) modal.style.display = config.openDisplay;
      requestAnimationFrame(applyOpenState);
    } else {
      applyOpenState();
    }

    return true;
  }

  function close(target, options) {
    var modal = getElement(target);
    if (!modal) return false;

    var config = buildConfig(modal, options);
    var state = stateMap.get(modal) || {};
    var wasOpen = modalStack.indexOf(modal) !== -1;

    removeModalFromStack(modal);
    setOpenState(modal, config, false);
    refreshBackgroundIsolation();

    if (config.lockScroll && wasOpen) {
      unlockBodyScroll();
    }

    if (wasOpen && config.restoreFocus && state.lastFocusedElement && typeof state.lastFocusedElement.focus === 'function') {
      state.lastFocusedElement.focus();
    }

    if (config.closeDelay > 0 && (config.hiddenAttribute || config.closeDisplay)) {
      window.setTimeout(function () {
        if (config.hiddenAttribute) modal.hidden = true;
        if (config.closeDisplay) modal.style.display = config.closeDisplay;
      }, config.closeDelay);
    } else {
      if (config.hiddenAttribute) modal.hidden = true;
      if (config.closeDisplay) modal.style.display = config.closeDisplay;
    }

    return true;
  }

  function bind(target, options) {
    var modal = getElement(target);
    if (!modal) return false;

    var config = buildConfig(modal, options);
    if (config._bound) return true;

    if (config.inertAttribute && !isOpen(modal, config)) {
      modal.setAttribute('inert', '');
    }

    if (config.closeSelector) {
      modal.addEventListener('click', function (event) {
        var current = configMap.get(modal) || config;
        var closeEl = event.target.closest(current.closeSelector);
        if (!closeEl) return;
        close(modal, current);
      });
    }

    config._bound = true;
    configMap.set(modal, config);
    return true;
  }

  function isOpen(target, options) {
    var modal = getElement(target);
    if (!modal) return false;
    var config = buildConfig(modal, options);

    if (config.openClass && modal.classList.contains(config.openClass)) return true;
    if (config.visibleClass && modal.classList.contains(config.visibleClass)) return true;
    if (config.ariaAttribute) return modal.getAttribute(config.ariaAttribute) === 'false';
    return false;
  }

  document.addEventListener('keydown', function (event) {
    if (!modalStack.length) return;

    var topModal = modalStack[modalStack.length - 1];
    var config = configMap.get(topModal) || defaults;

    if (event.key === 'Escape' && config.closeOnEscape) {
      event.preventDefault();
      close(topModal, config);
      return;
    }

    if (event.key !== 'Tab' || !config.trapFocus) return;

    var focusables = getFocusableElements(topModal);
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.SAModalController = {
    bind: bind,
    open: open,
    close: close,
    isOpen: isOpen
  };
})();
